import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { io } from 'socket.io-client';
import { registerPushSubscription } from '../utils/pushSubscribe';
import { authHeaders } from '../utils/authHeaders';

function decodeToken() {
  try {
    const token = localStorage.getItem('mfg_token');
    if (!token) return null;
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const json = decodeURIComponent(
      atob(base64).split('').map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)).join('')
    );
    const payload = JSON.parse(json);
    if (!payload.name) payload.name = payload.username;
    return payload;
  } catch { return null; }
}

export const ChatContext = createContext(null);

function fireOsNotification(msg, isMention = false) {
  if (!('Notification' in window)) return;
  if (Notification.permission === 'default') {
    Notification.requestPermission();
    return;
  }
  if (Notification.permission !== 'granted') return;
  const sender = msg.user?.name || msg.user?.username || '새 메시지';
  const body = (() => {
    try {
      const parsed = JSON.parse(msg.content);
      if (parsed?.url) return `[파일] ${parsed.name || ''}`;
    } catch {}
    return msg.content || '';
  })();
  const n = new Notification(isMention ? `@멘션 — ${sender}` : sender, {
    body: body.slice(0, 80),
    icon: '/favicon.svg',
    tag: `chat-${msg.channelId}`,
    renotify: true,
  });
  n.onclick = () => { window.focus(); n.close(); };
}

export function ChatProvider({ children }) {
  const socketRef = useRef(null);
  const [socket, setSocket] = useState(null);
  const activeChannelRef = useRef(null);
  const isTypingRef = useRef(false);
  const typingTimerRef = useRef(null);
  const timerRefs = useRef({});
  const chatOpenRef = useRef(false);
  const pushRequestedRef = useRef(false);
  const isInitialLoadRef = useRef(false);

  const me = decodeToken();

  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState(null);
  const [messages, setMessages] = useState([]);
  const [typingUsers, setTypingUsers] = useState({});
  const [loading, setLoading] = useState(false);
  const [totalUnread, setTotalUnread] = useState(0);
  const [toasts, setToasts] = useState([]);
  const [newMsg, setNewMsg] = useState('');
  const [uploading, setUploading] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [showMembers, setShowMembers] = useState(false);
  const [requestChannelId, setRequestChannelId] = useState(null);
  const [meetingChannel, setMeetingChannel] = useState(null);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [readMap, setReadMap] = useState({});         // {userId: lastReadAt} for active channel
  const [channelMemberCount, setChannelMemberCount] = useState(0);
  const msgsCacheRef = useRef({});

  // Google Chat 전역 unread (GoogleChatPanel 밖에서도 뱃지/알림 유지)
  const [gchatUnreadSpaces, setGchatUnreadSpaces] = useState({});
  const gchatActiveSpaceRef = useRef(null);  // 현재 보고 있는 스페이스 ID
  const gchatSpaceNamesRef = useRef({});     // spaceId → displayName

  const loadChannels = useCallback(() => {
    fetch('/api/chat/channels', { headers: authHeaders() })
      .then(r => r.json())
      .then(data => setChannels(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('mfg_token');
    if (!token) return;
    const socket = io({ auth: { token }, transports: ['websocket'] });
    socketRef.current = socket;
    setSocket(socket);

    socket.on('new_message', msg => {
      if (activeChannelRef.current?.id === msg.channelId) {
        setMessages(prev => {
          const updated = [...prev, msg];
          msgsCacheRef.current[msg.channelId] = updated;
          return updated;
        });
        if (chatOpenRef.current) socket.emit('mark_read', { channelId: msg.channelId });
      }
      // 해당 채널을 목록 맨 위로
      setChannels(prev => {
        const idx = prev.findIndex(ch => Number(ch.id) === Number(msg.channelId));
        if (idx <= 0) return prev;
        const updated = [...prev];
        const [ch] = updated.splice(idx, 1);
        return [{ ...ch, lastMessage: { content: msg.content, createdAt: msg.createdAt, userId: msg.userId, user: msg.user } }, ...updated];
      });
    });

    socket.on('new_message_notify', msg => {
      const myId = decodeToken()?.id;
      if (msg.userId === myId) return;
      if (activeChannelRef.current?.id !== msg.channelId) {
        setChannels(prev => {
          const idx = prev.findIndex(ch => Number(ch.id) === Number(msg.channelId));
          if (idx < 0) return prev;
          const updated = [...prev];
          const [ch] = updated.splice(idx, 1);
          return [{ ...ch, unreadCount: (ch.unreadCount || 0) + 1, lastMessage: { content: msg.content, createdAt: msg.createdAt, userId: msg.userId, user: msg.user } }, ...updated];
        });
      }
      const isViewing = chatOpenRef.current && activeChannelRef.current?.id === msg.channelId;
      if (!isViewing) {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev.slice(-2), { id, msg }]);
        timerRefs.current[id] = setTimeout(() => {
          setToasts(prev => prev.filter(t => t.id !== id));
          delete timerRefs.current[id];
        }, 5000);
        // OS 알림
        fireOsNotification(msg);
      }
    });

    socket.on('user_typing', ({ userId, userName }) =>
      setTypingUsers(prev => ({ ...prev, [userId]: userName })));
    socket.on('user_stop_typing', ({ userId }) =>
      setTypingUsers(prev => { const n = { ...prev }; delete n[userId]; return n; }));
    socket.on('message_deleted', ({ messageId }) =>
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, deleted: true } : m)));

    socket.on('reaction_update', ({ messageId, reactions }) =>
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, reactions } : m)));

    socket.on('message_edited', ({ messageId, content }) =>
      setMessages(prev => prev.map(m => m.id === messageId ? { ...m, content, edited: true } : m)));

    // Google Chat 전역 리스너 — GoogleChatPanel이 마운트되지 않아도 항상 동작
    socket.on('google_chat_messages', ({ spaceId, messages: incoming }) => {
      // 현재 보고 있는 스페이스면 GoogleChatPanel이 처리
      if (gchatActiveSpaceRef.current === spaceId) return;

      const myGchatId = localStorage.getItem('gchat_my_id');
      const fromOthers = incoming.filter(m => {
        if (!myGchatId) return true;
        return m.senderName !== `users/${myGchatId}`;
      });
      if (!fromOthers.length) return;

      setGchatUnreadSpaces(prev => ({ ...prev, [spaceId]: (prev[spaceId] || 0) + fromOthers.length }));

      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const spaceName = gchatSpaceNamesRef.current[spaceId] || 'Google Chat';
        fromOthers.slice(0, 2).forEach(msg => {
          new Notification(`${msg.sender} — ${spaceName}`, {
            body: msg.text?.slice(0, 100) || '📎 파일',
            icon: '/favicon.svg',
            tag: `gchat-${msg.name}`,
          });
        });
      }
    });

    socket.on('joined', ({ readMap: rm, memberCount }) => {
      if (rm) setReadMap(rm);
      if (memberCount != null) setChannelMemberCount(memberCount);
    });

    socket.on('read_receipt', ({ channelId, userId, readAt }) => {
      if (activeChannelRef.current?.id === channelId) {
        setReadMap(prev => ({ ...prev, [userId]: readAt }));
      }
    });

    socket.on('mentioned', msg => {
      const isViewing = chatOpenRef.current && activeChannelRef.current?.id === msg.channelId;
      if (!isViewing) {
        const id = Date.now() + Math.random();
        setToasts(prev => [...prev.slice(-2), { id, msg, isMention: true }]);
        timerRefs.current[id] = setTimeout(() => {
          setToasts(prev => prev.filter(t => t.id !== id));
          delete timerRefs.current[id];
        }, 7000);
        // OS 알림 (멘션 강조)
        fireOsNotification(msg, true);
      }
    });

    return () => socket.disconnect();
  }, []);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  useEffect(() => {
    const total = channels.reduce((sum, ch) => sum + (ch.unreadCount || 0), 0);
    setTotalUnread(total);
    // 브라우저 탭 타이틀 뱃지
    const base = '프린만 업무관리시스템';
    document.title = total > 0 ? `(${total > 99 ? '99+' : total}) ${base}` : base;
  }, [channels]);

  // 알림 권한 조기 요청 (사용자 첫 액션 타이밍에)
  useEffect(() => {
    if ('Notification' in window && Notification.permission === 'default') {
      const onFirst = () => { Notification.requestPermission(); document.removeEventListener('click', onFirst); };
      document.addEventListener('click', onFirst, { once: true });
      return () => document.removeEventListener('click', onFirst);
    }
  }, []);

  const selectChannel = useCallback(async ch => {
    activeChannelRef.current = ch;
    setActiveChannel(ch);
    setTypingUsers({});
    setShowMembers(false);
    setReadMap({});
    setChannelMemberCount(0);

    // 캐시 히트: 즉시 표시 (로딩 없음)
    const cached = msgsCacheRef.current[ch.id];
    if (cached) {
      setMessages(cached);
      setLoading(false);
    } else {
      setMessages([]);
      setLoading(true);
    }

    try {
      const res = await fetch(`/api/chat/channels/${ch.id}/messages`, { headers: authHeaders() });
      const data = await res.json();
      const msgs = data.messages || [];
      msgsCacheRef.current[ch.id] = msgs;
      isInitialLoadRef.current = true;
      if (activeChannelRef.current?.id === ch.id) setMessages(msgs);
    } catch {
      if (activeChannelRef.current?.id === ch.id) setMessages([]);
    } finally {
      if (activeChannelRef.current?.id === ch.id) setLoading(false);
    }

    socketRef.current?.emit('join_channel', ch.id);
    setChannels(prev => prev.map(c => c.id === ch.id ? { ...c, unreadCount: 0 } : c));
  }, []);

  const stopTyping = useCallback(() => {
    if (isTypingRef.current && activeChannelRef.current) {
      socketRef.current?.emit('typing_stop', { channelId: activeChannelRef.current.id });
      isTypingRef.current = false;
    }
    clearTimeout(typingTimerRef.current);
  }, []);

  const handleInputChange = useCallback(e => {
    setNewMsg(e.target.value);
    if (!activeChannelRef.current) return;
    if (!isTypingRef.current) {
      socketRef.current?.emit('typing_start', { channelId: activeChannelRef.current.id });
      isTypingRef.current = true;
    }
    clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(stopTyping, 2000);
  }, [stopTyping]);

  const sendMessage = useCallback(() => {
    const content = newMsg.trim();
    if (!content || !activeChannelRef.current) return;
    socketRef.current?.emit('send_message', { channelId: activeChannelRef.current.id, content });
    setNewMsg('');
    stopTyping();
  }, [newMsg, stopTyping]);

  const handleFileSelect = useCallback(async (file) => {
    if (!file || !activeChannelRef.current) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = localStorage.getItem('mfg_token');
      const res = await fetch('/api/chat/upload', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      if (data.url) {
        socketRef.current?.emit('send_message', {
          channelId: activeChannelRef.current.id,
          content: JSON.stringify({ url: data.url, name: data.name, size: data.size, mime: data.mime }),
          type: 'file',
        });
      }
    } catch {} finally { setUploading(false); }
  }, []);

  const createChannel = useCallback(async form => {
    const res = await fetch('/api/chat/channels', { method: 'POST', headers: authHeaders(), body: JSON.stringify(form) });
    const created = await res.json();
    if (created?.id) setChannels(prev => [...prev, { ...created, unreadCount: 0 }]);
  }, []);

  const renameChannel = useCallback(async (id, { name, description }) => {
    const body = {};
    if (name !== undefined) body.name = name;
    if (description !== undefined) body.description = description;
    const res = await fetch(`/api/chat/channels/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body) });
    const updated = await res.json();
    if (updated?.id) {
      setChannels(prev => prev.map(ch => ch.id === id ? { ...ch, name: updated.name, description: updated.description } : ch));
      if (activeChannelRef.current?.id === id) setActiveChannel(a => ({ ...a, name: updated.name, description: updated.description }));
    }
  }, []);

  const reorderChannel = useCallback(async (id, sortOrder) => {
    await fetch(`/api/chat/channels/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify({ sortOrder }) });
  }, []);

  const deleteChannel = useCallback(async ch => {
    if (!confirm(`'${ch.name}' 채널을 삭제하시겠습니까?`)) return;
    await fetch(`/api/chat/channels/${ch.id}`, { method: 'DELETE', headers: authHeaders() });
    setChannels(prev => prev.filter(c => c.id !== ch.id));
    if (activeChannelRef.current?.id === ch.id) {
      setActiveChannel(null);
      activeChannelRef.current = null;
    }
  }, []);

  const dismissToast = useCallback(id => {
    clearTimeout(timerRefs.current[id]);
    delete timerRefs.current[id];
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const openToastChannel = useCallback((channelId, toastId) => {
    dismissToast(toastId);
    setRequestChannelId(channelId);
  }, [dismissToast]);

  const sendReaction = useCallback((messageId, emoji) => {
    socketRef.current?.emit('add_reaction', { messageId, emoji });
  }, []);

  const editMessage = useCallback((messageId, content) => {
    socketRef.current?.emit('edit_message', { messageId, content });
  }, []);

  const openDm = useCallback(async (targetUser) => {
    const res = await fetch(`/api/chat/dm/${targetUser.id}`, { headers: authHeaders() });
    if (!res.ok) return;
    const channel = await res.json();
    if (!channel?.id) return;
    setChannels(prev => {
      const exists = prev.find(c => c.id === channel.id);
      if (exists) return prev.map(c => c.id === channel.id ? { ...c, unreadCount: 0, dmPartner: targetUser } : c);
      return [{ ...channel, unreadCount: 0, dmPartner: targetUser }, ...prev];
    });
    selectChannel({ ...channel, dmPartner: targetUser });
  }, [selectChannel]);

  const notifyChatOpen = useCallback((isOpen) => {
    chatOpenRef.current = isOpen;
    if (isOpen && !pushRequestedRef.current) {
      const token = localStorage.getItem('mfg_token');
      if (token) { pushRequestedRef.current = true; registerPushSubscription(); }
    }
  }, []);

  return (
    <ChatContext.Provider value={{
      me,
      channels, setChannels, activeChannel, messages, typingUsers, loading,
      totalUnread, toasts, newMsg, uploading,
      showNewModal, setShowNewModal,
      renameTarget, setRenameTarget,
      showMembers, setShowMembers,
      requestChannelId, setRequestChannelId,
      meetingChannel, setMeetingChannel,
      meetingOpen, setMeetingOpen,
      isInitialLoadRef,
      selectChannel, handleInputChange, sendMessage, handleFileSelect,
      createChannel, renameChannel, deleteChannel, openDm,
      loadChannels, dismissToast, openToastChannel, notifyChatOpen,
      reorderChannel, sendReaction, editMessage,
      setNewMsg,
      readMap, channelMemberCount,
      socketRef, socket,
      gchatUnreadSpaces, setGchatUnreadSpaces,
      gchatActiveSpaceRef, gchatSpaceNamesRef,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

